import { z } from "zod";

export const periodoDashboardSchema = z.enum(["semana", "mes", "30d"]);

export const statusConteudoSchema = z.enum([
  "rascunho",
  "em_producao",
  "revisao",
  "aprovado",
  "publicado",
  "pausado",
]);

export const formatoConteudoSchema = z.enum([
  "feed",
  "carrossel",
  "reels",
  "stories",
]);

export const gargaloProdutividadeSchema = z.object({
  conteudoId: z.string().uuid(),
  titulo: z.string(),
  status: statusConteudoSchema,
  diasParado: z.number().int().nonnegative(),
});

export const throughputPessoaSchema = z.object({
  responsavelId: z.string().uuid().nullable(),
  responsavelNome: z.string().nullable(),
  quantidade: z.number().int().nonnegative(),
});

export const produtividadeSchema = z.object({
  tempoCicloMedioDias: z.number().nullable(),
  postsPorStatus: z.record(z.string(), z.number().int().nonnegative()),
  throughputPorPessoa: z.array(throughputPessoaSchema),
  gargalos: z.array(gargaloProdutividadeSchema),
  periodo: periodoDashboardSchema,
});

export const ocupacaoPessoaSchema = z.object({
  responsavelId: z.string().uuid().nullable(),
  responsavelNome: z.string().nullable(),
  cargaAtual: z.number().int().nonnegative(),
  capacidadePeriodo: z.number().int().nonnegative(),
  percentualOcupacao: z.number().nonnegative(),
  sobrecarga: z.boolean(),
});

export const ocupacaoSchema = z.object({
  cargaAtual: z.number().int().nonnegative(),
  capacidadePeriodo: z.number().int().nonnegative(),
  percentualOcupacao: z.number().nonnegative(),
  sobrecarga: z.boolean(),
  porPessoa: z.array(ocupacaoPessoaSchema),
  periodo: periodoDashboardSchema,
});

export const postPerformanceSchema = z.object({
  conteudoId: z.string().uuid(),
  titulo: z.string(),
  formato: formatoConteudoSchema,
  alcance: z.number().int().nonnegative(),
  engajamento: z.number().int().nonnegative(),
  taxaEngajamento: z.number().nullable(),
  salvamentos: z.number().int().nonnegative(),
  compartilhamentos: z.number().int().nonnegative(),
  publicadoEm: z.string().datetime().nullable(),
});

export const serieDiariaPontoSchema = z.object({
  data: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  alcance: z.number().int().nonnegative(),
  engajamento: z.number().int().nonnegative(),
  publicacoes: z.number().int().nonnegative(),
});

export const heatmapSlotSchema = z.object({
  diaSemana: z.number().int().min(0).max(6),
  hora: z.number().int().min(0).max(23),
  engajamento: z.number().int().nonnegative(),
  publicacoes: z.number().int().nonnegative(),
});

export const deltaComparativoSchema = z.object({
  atual: z.number(),
  anterior: z.number(),
  deltaPercentual: z.number().nullable(),
});

export const comparativoPeriodoSchema = z.object({
  alcance: deltaComparativoSchema,
  engajamento: deltaComparativoSchema,
  publicados: deltaComparativoSchema,
  fidelidade: deltaComparativoSchema,
});

export const performanceSchema = z.object({
  topPosts: z.array(postPerformanceSchema).max(10),
  alcanceMedio: z.number().nonnegative(),
  taxaEngajamento: z.number().nullable(),
  totalSalvamentos: z.number().int().nonnegative(),
  totalCompartilhamentos: z.number().int().nonnegative(),
  totalImpressoes: z.number().int().nonnegative(),
  melhorFormato: formatoConteudoSchema.nullable(),
  melhorHorario: z.number().int().min(0).max(23).nullable(),
  crescimentoSeguidores: z.number().nullable(),
  serieDiaria: z.array(serieDiariaPontoSchema),
  heatmapHorario: z.array(heatmapSlotSchema),
  comparativoPeriodo: comparativoPeriodoSchema.nullable(),
  metaConectada: z.boolean(),
  temMetricas: z.boolean(),
  ultimaColetaEm: z.string().datetime().nullable(),
  de: z.string().datetime(),
  ate: z.string().datetime(),
});

export const resultadoColetaMetricasSchema = z.object({
  coletados: z.number().int().nonnegative(),
  ignorados: z.number().int().nonnegative(),
  falhas: z.number().int().nonnegative(),
  sincronizados: z.number().int().nonnegative(),
});

export const fidelidadeSchema = z.object({
  planejados: z.number().int().nonnegative(),
  publicadosNoPrazo: z.number().int().nonnegative(),
  taxaFidelidade: z.number().min(0).max(1).nullable(),
  mes: z.string().regex(/^\d{4}-\d{2}$/),
});

export const falhaPublicacaoResumoSchema = z.object({
  id: z.string().uuid(),
  nome: z.string(),
  plataforma: z.string(),
  motivoFalha: z.string().nullable(),
  atualizadoEm: z.string().datetime(),
});

export const publicacaoResumoSchema = z.object({
  porStatus: z.record(z.string(), z.number().int().nonnegative()),
  porPlataforma: z.record(z.string(), z.number().int().nonnegative()),
  falhasRecentes: z.array(falhaPublicacaoResumoSchema).max(10),
  totalFalhas: z.number().int().nonnegative(),
});

export const alertaDashboardSchema = z.object({
  id: z.string(),
  severidade: z.enum(["alta", "media", "baixa"]),
  titulo: z.string(),
  descricao: z.string(),
  acaoHref: z.string().nullable(),
});

export const resumoDashboardSchema = z.object({
  produtividade: produtividadeSchema,
  ocupacao: ocupacaoSchema,
  performance: performanceSchema,
  fidelidade: fidelidadeSchema,
  publicacaoResumo: publicacaoResumoSchema,
  alertas: z.array(alertaDashboardSchema),
});

export type PeriodoDashboard = z.infer<typeof periodoDashboardSchema>;
export type Produtividade = z.infer<typeof produtividadeSchema>;
export type OcupacaoPessoa = z.infer<typeof ocupacaoPessoaSchema>;
export type Ocupacao = z.infer<typeof ocupacaoSchema>;
export type Performance = z.infer<typeof performanceSchema>;
export type Fidelidade = z.infer<typeof fidelidadeSchema>;
export type PublicacaoResumo = z.infer<typeof publicacaoResumoSchema>;
export type AlertaDashboard = z.infer<typeof alertaDashboardSchema>;
export type ResumoDashboard = z.infer<typeof resumoDashboardSchema>;
export type ResultadoColetaMetricas = z.infer<
  typeof resultadoColetaMetricasSchema
>;
